
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useDisciplines } from "@/hooks/useDisciplines";
import { X, Filter } from "lucide-react";

interface SearchFiltersProps {
  filters: {
    discipline?: string;
    nodeType?: string;
    tags?: string[];
  };
  onFiltersChange: (filters: any) => void;
}

export const SearchFilters = ({ filters, onFiltersChange }: SearchFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: disciplines } = useDisciplines();

  const nodeTypes = [
    { value: 'article', label: 'Article' },
    { value: 'lesson', label: 'Lesson' },
    { value: 'concept', label: 'Concept' },
    { value: 'reference', label: 'Reference' },
  ];

  const availableTags = [
    'philosophy', 'theology', 'ethics', 'logic', 'metaphysics',
    'history', 'law', 'mathematics', 'science', 'physics',
    'biology', 'chemistry', 'engineering', 'health', 'medicine'
  ];

  const handleDisciplineChange = (value: string) => {
    onFiltersChange({
      ...filters,
      discipline: value === 'all' ? undefined : value,
    });
  };

  const handleNodeTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      nodeType: value === 'all' ? undefined : value,
    });
  };

  const handleTagToggle = (tag: string) => {
    const currentTags = filters.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    
    onFiltersChange({
      ...filters,
      tags: newTags.length > 0 ? newTags : undefined,
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  const hasActiveFilters = filters.discipline || filters.nodeType || (filters.tags && filters.tags.length > 0);

  // Filter out any disciplines that might have empty or invalid IDs
  const validDisciplines = disciplines?.filter(discipline => discipline.id && discipline.id.trim() !== '') || [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Less' : 'More'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Discipline Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Discipline</label>
          <Select value={filters.discipline || 'all'} onValueChange={handleDisciplineChange}>
            <SelectTrigger>
              <SelectValue placeholder="All disciplines" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All disciplines</SelectItem>
              {validDisciplines.map((discipline) => (
                <SelectItem key={discipline.id} value={discipline.id}>
                  {discipline.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Node Type Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Content Type</label>
          <Select value={filters.nodeType || 'all'} onValueChange={handleNodeTypeChange}>
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {nodeTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tags Filter - Show when expanded */}
        {isExpanded && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Tags</label>
            <div className="grid grid-cols-2 gap-2">
              {availableTags.map((tag) => (
                <div key={tag} className="flex items-center space-x-2">
                  <Checkbox
                    id={tag}
                    checked={filters.tags?.includes(tag) || false}
                    onCheckedChange={() => handleTagToggle(tag)}
                  />
                  <label htmlFor={tag} className="text-sm capitalize">
                    {tag}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="space-y-2 pt-2 border-t">
            <label className="text-sm font-medium">Active Filters</label>
            <div className="flex flex-wrap gap-2">
              {filters.discipline && validDisciplines && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  {validDisciplines.find(d => d.id === filters.discipline)?.name}
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => handleDisciplineChange('all')}
                  />
                </Badge>
              )}
              {filters.nodeType && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  {nodeTypes.find(t => t.value === filters.nodeType)?.label}
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => handleNodeTypeChange('all')}
                  />
                </Badge>
              )}
              {filters.tags?.map((tag) => (
                <Badge key={tag} variant="secondary" className="flex items-center gap-1 capitalize">
                  {tag}
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => handleTagToggle(tag)}
                  />
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
